import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import EnhancedDateTimePicker from './EnhancedDateTimePicker';

// Compact Email Status Component
function EmailConnectionStatus({ connectionStatus }) {
  const getStatusColor = () => {
    // If there's an auto-disconnect reason, show as disconnected regardless of connected status
    if (connectionStatus?.autoDisconnectedReason) return 'bg-red-500';
    if (connectionStatus?.connected === true) return 'bg-emerald-500';
    if (connectionStatus?.connected === false) return 'bg-red-500';
    return 'bg-yellow-500';
  };

  const getStatusIcon = () => {
    // If there's an auto-disconnect reason, show as disconnected regardless of connected status
    if (connectionStatus?.autoDisconnectedReason) return '✗';
    if (connectionStatus?.connected === true) return '✓';
    if (connectionStatus?.connected === false) return '✗';
    return '⟳';
  };

  return (
    <div className="relative">
      <div 
        className={`w-8 h-8 rounded-full ${getStatusColor()} flex items-center justify-center text-white text-sm font-medium cursor-pointer transition-all hover:scale-110 group`}
        title="Calendar Connection Status"
      >
        {getStatusIcon()}
        
        {/* Tooltip - Only shows when hovering over the status circle */}
        <div className="absolute top-10 right-0 bg-klorah-card border border-klorah-border rounded-lg p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-200 z-50 min-w-64 pointer-events-none">
          <div className="text-sm text-white font-medium mb-1">
            Calendar Connection
          </div>
          <div className="text-xs text-klorah-text-light">
            {connectionStatus?.email || 'No email connected'}
          </div>
          <div className={`text-xs mt-1 ${connectionStatus?.autoDisconnectedReason ? 'text-red-400' : connectionStatus?.connected === true ? 'text-emerald-400' : connectionStatus?.connected === false ? 'text-red-400' : 'text-yellow-400'}`}>
            {connectionStatus?.autoDisconnectedReason ? 
              `Disconnected: ${connectionStatus.autoDisconnectedReason}` :
              connectionStatus?.connected === true ? 'Connected & Syncing' : 
              connectionStatus?.connected === false ? 'Not Connected' : 
              'Authorization Pending'}
          </div>
        </div>
      </div>
    </div>
  );
}

// Modern Calendar Header Component
function CalendarHeader({ currentDate, onDateChange, onViewChange, currentView, connectionStatus, onConnect, onClearTokens }) {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    if (currentView === 'week') {
      newDate.setDate(currentDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(currentDate.getMonth() + direction);
    }
    onDateChange(newDate);
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  const getNavigationLabel = () => {
    if (currentView === 'week') {
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - currentDate.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
      } else {
        return `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()} - ${monthNames[weekEnd.getMonth()]} ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
      }
    } else {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  return (
    <div className="bg-klorah-card/50 backdrop-blur-xl border border-klorah-border rounded-xl p-4 mb-6">
      {/* Top Row - Title and Email Status */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Calendar Dashboard</h1>
        <EmailConnectionStatus connectionStatus={connectionStatus} />
      </div>

      {/* Navigation and Controls Row */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* Left Side - Date Navigation (Google Calendar Style) */}
        <div className="flex items-center space-x-4">
          {/* Today Button */}
          <button
            onClick={goToToday}
            className="px-4 py-2 text-sm text-klorah-primary border border-klorah-primary rounded-lg hover:bg-klorah-primary/10 transition-all font-medium"
          >
            Today
          </button>

          {/* Navigation Arrows */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 text-klorah-text hover:text-white hover:bg-klorah-primary/20 rounded-lg transition-all"
              title={`Previous ${currentView}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 text-klorah-text hover:text-white hover:bg-klorah-primary/20 rounded-lg transition-all"
              title={`Next ${currentView}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Current Period Label */}
          <div className="text-white font-medium text-lg">
            {getNavigationLabel()}
          </div>
        </div>

        {/* Right Side - View Controls and Actions */}
        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex bg-klorah-dark border border-klorah-border rounded-lg overflow-hidden">
            <button
              onClick={() => onViewChange('month')}
              className={`px-4 py-2 text-sm transition-all ${
                currentView === 'month' 
                  ? 'bg-klorah-primary text-white' 
                  : 'text-klorah-text hover:text-white hover:bg-klorah-primary/20'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => onViewChange('week')}
              className={`px-4 py-2 text-sm transition-all ${
                currentView === 'week' 
                  ? 'bg-klorah-primary text-white' 
                  : 'text-klorah-text hover:text-white hover:bg-klorah-primary/20'
              }`}
            >
              Week
            </button>
          </div>

          {/* Connect Calendar Button */}
          {(connectionStatus?.connected !== true || connectionStatus?.autoDisconnectedReason) && (
            <div className="flex gap-2">
              <button
                onClick={onConnect}
                className="px-4 py-2 bg-klorah-primary text-white rounded-lg hover:bg-klorah-primary/80 transition-all text-sm font-medium"
              >
                {connectionStatus?.autoDisconnectedReason ? 'Reconnect Calendar' : 'Connect Calendar'}
              </button>
              
              {/* Clear Tokens Button - Show when there are token issues */}
              {connectionStatus?.autoDisconnectedReason && onClearTokens && (
                <button
                  onClick={onClearTokens}
                  className="px-3 py-2 bg-klorah-border text-klorah-text-light rounded-lg hover:bg-klorah-border/80 transition-all text-sm font-medium"
                  title="Clear stored tokens to fix connection issues"
                >
                  Clear Tokens
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Enhanced Event Duration Component
function EventDurationSelector({ startDate, endDate, onStartChange, onEndChange, isAllDay, onAllDayChange }) {
  const [duration, setDuration] = useState('1h');

  const durationPresets = [
    { label: '15 min', minutes: 15 },
    { label: '30 min', minutes: 30 },
    { label: '1 hour', minutes: 60 },
    { label: '2 hours', minutes: 120 },
    { label: '4 hours', minutes: 240 },
  ];

  const handleDurationPreset = (minutes) => {
    if (startDate) {
      const start = new Date(startDate);
      const end = new Date(start.getTime() + minutes * 60 * 1000);
      onEndChange(end.toISOString().slice(0, 16));
    }
  };

  return (
    <div className="space-y-4">
      {/* All Day Toggle */}
      <div className="flex items-center justify-between p-3 bg-klorah-dark border border-klorah-border rounded-xl">
        <span className="text-white text-sm">All day event</span>
        <button
          type="button"
          onClick={() => onAllDayChange(!isAllDay)}
          className={`relative w-12 h-6 rounded-full transition-all ${
            isAllDay ? 'bg-klorah-primary' : 'bg-klorah-border'
          }`}
        >
          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
            isAllDay ? 'translate-x-6' : 'translate-x-0.5'
          }`} />
        </button>
      </div>

      {/* Start Date/Time */}
      <EnhancedDateTimePicker
        value={startDate}
        onChange={onStartChange}
        label="Start"
        required
        isAllDay={isAllDay}
        placeholder="Select start date and time"
      />

      {/* End Date/Time - Only show if not all day */}
      {!isAllDay && (
        <>
          <EnhancedDateTimePicker
            value={endDate}
            onChange={onEndChange}
            label="End"
            required
            minDate={startDate}
            isAllDay={isAllDay}
            placeholder="Select end date and time"
          />

          {/* Duration Presets */}
          <div className="space-y-2">
            <label className="text-xs text-klorah-text-light">Quick duration:</label>
            <div className="flex flex-wrap gap-2">
              {durationPresets.map(preset => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handleDurationPreset(preset.minutes)}
                  className="px-3 py-1 text-xs bg-klorah-primary/20 text-klorah-primary rounded-lg hover:bg-klorah-primary/30 transition-all"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Interactive Calendar Grid Component
function InteractiveCalendarGrid({ 
  currentDate, 
  events, 
  onEventClick, 
  onDateClick, 
  onEventCreate,
  currentView 
}) {
  const [dragState, setDragState] = useState(null);
  const [hoverCell, setHoverCell] = useState(null);

  // Debug logging for events
  console.log('InteractiveCalendarGrid received events:', events);
  console.log('Number of events in grid:', events?.length || 0);
  console.log('Current date:', currentDate);
  console.log('Current view:', currentView);

  // Get calendar days for month view
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  // Get week days for week view
  const getWeekDays = () => {
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Handle intelligent drag-to-create events
  const handleMouseDown = (date, hour, event) => {
    if (event.target.closest('.event-item')) return; // Don't start drag on existing events
    
    const startTime = new Date(date);
    startTime.setHours(hour, 0, 0, 0);
    
    setDragState({
      start: startTime,
      end: new Date(startTime.getTime() + 60 * 60 * 1000), // Default 1 hour
      isCreating: true,
      startDate: date,
      startHour: hour
    });
    
    event.preventDefault();
  };

  const handleMouseMove = (date, hour, event) => {
    if (!dragState?.isCreating) {
      setHoverCell({ date, hour });
      return;
    }
    
    const endTime = new Date(date);
    endTime.setHours(hour + 1, 0, 0, 0);
    
    // Allow dragging across days and times
    if (endTime >= dragState.start) {
      setDragState(prev => ({ ...prev, end: endTime, endDate: date, endHour: hour + 1 }));
    }
  };

  const handleMouseUp = () => {
    if (dragState?.isCreating) {
      onEventCreate({
        startDate: dragState.start.toISOString().slice(0, 16),
        endDate: dragState.end.toISOString().slice(0, 16),
        title: '',
        description: ''
      });
    }
    setDragState(null);
  };

  const handleMouseLeave = () => {
    setHoverCell(null);
  };

  // Check if a cell is part of current drag selection
  const isDragSelected = (date, hour) => {
    if (!dragState?.isCreating) return false;
    
    const cellTime = new Date(date);
    cellTime.setHours(hour, 0, 0, 0);
    
    return cellTime >= dragState.start && cellTime < dragState.end;
  };

  const days = currentView === 'week' ? getWeekDays() : getCalendarDays();
  const today = new Date();

  if (currentView === 'week') {
    // Week View with Hour-by-Hour Drag Support
    return (
      <div 
        className="bg-klorah-card/30 backdrop-blur-xl border border-klorah-border rounded-xl overflow-hidden"
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* Week Header */}
        <div className="grid grid-cols-8 bg-klorah-dark/50">
          <div className="p-3 text-xs text-klorah-text-light border-r border-klorah-border/30">
            Time
          </div>
          {days.map(day => (
            <div key={day.toISOString()} className="p-3 text-center border-r border-klorah-border/30 last:border-r-0">
              <div className="text-xs text-klorah-text-light">
                {day.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className={`text-sm font-medium ${
                day.toDateString() === today.toDateString() 
                  ? 'text-klorah-primary' 
                  : 'text-white'
              }`}>
                {day.getDate()}
              </div>
            </div>
          ))}
        </div>

        {/* Time Slots */}
        <div className="max-h-[600px] overflow-y-auto">
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} className="grid grid-cols-8 border-b border-klorah-border/20">
              <div className="p-2 text-xs text-klorah-text-light bg-klorah-dark/30 border-r border-klorah-border/30 flex items-center">
                {hour === 0 ? '12:00 AM' : 
                 hour < 12 ? `${hour}:00 AM` : 
                 hour === 12 ? '12:00 PM' : 
                 `${hour - 12}:00 PM`}
              </div>
              {days.map(day => {
                const cellEvents = events.filter(event => {
                  // Handle Google Calendar API format where start/end are objects
                  const eventStart = event.start?.dateTime || event.start?.date || event.startDate;
                  const startDate = new Date(eventStart);
                  return startDate.toDateString() === day.toDateString() && 
                         startDate.getHours() === hour;
                });
                
                // Debug logging for week view event filtering
                if (cellEvents.length > 0) {
                  console.log(`Week view: ${day.toDateString()} at ${hour}:00 has ${cellEvents.length} events:`, cellEvents);
                }
                
                return (
                  <div
                    key={`${day.toISOString()}-${hour}`}
                    className={`relative h-12 border-r border-klorah-border/20 last:border-r-0 cursor-pointer transition-all ${
                      isDragSelected(day, hour) ? 'bg-klorah-primary/30' :
                      hoverCell?.date?.toDateString() === day.toDateString() && hoverCell?.hour === hour ? 'bg-klorah-primary/10' :
                      'hover:bg-klorah-primary/5'
                    }`}
                    onMouseDown={(e) => handleMouseDown(day, hour, e)}
                    onMouseMove={(e) => handleMouseMove(day, hour, e)}
                  >
                    {/* Events for this time slot */}
                    {cellEvents.map(event => (
                      <div
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick(event);
                        }}
                        className="event-item absolute inset-x-1 top-1 bg-klorah-primary/90 text-white text-xs p-1 rounded cursor-pointer hover:bg-klorah-primary transition-all z-10 truncate"
                      >
                        {event.title || event.summary || 'Untitled Event'}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Month View with Click-to-Create
  return (
    <div 
      className="bg-klorah-card/30 backdrop-blur-xl border border-klorah-border rounded-xl overflow-hidden"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Days of Week Header */}
      <div className="grid grid-cols-7 bg-klorah-dark/50">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-3 text-center text-xs font-medium text-klorah-text-light border-r border-klorah-border/30 last:border-r-0">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const isCurrentMonth = day.getMonth() === currentDate.getMonth();
          const isToday = day.toDateString() === today.toDateString();
          const dayEvents = events.filter(event => {
            // Handle Google Calendar API format where start/end are objects
            const startDate = event.start?.dateTime || event.start?.date || event.startDate;
            return new Date(startDate).toDateString() === day.toDateString();
          });

          // Debug logging for event filtering
          if (dayEvents.length > 0) {
            console.log(`Day ${day.toDateString()} has ${dayEvents.length} events:`, dayEvents);
          }

          return (
            <div
              key={index}
              className={`relative h-24 border-r border-b border-klorah-border/20 last:border-r-0 cursor-pointer transition-all ${
                isCurrentMonth ? 'bg-transparent hover:bg-klorah-primary/5' : 'bg-klorah-dark/20'
              }`}
              onClick={() => isCurrentMonth && onDateClick(day)}
            >
              {/* Date Number */}
              <div className="p-2">
                <span className={`text-sm font-medium ${
                  isToday ? 'bg-klorah-primary text-white rounded-full w-6 h-6 flex items-center justify-center' :
                  isCurrentMonth ? 'text-white' : 'text-klorah-text-light'
                }`}>
                  {day.getDate()}
                </span>
              </div>

              {/* Events */}
              <div className="absolute inset-x-1 top-8 space-y-1">
                {dayEvents.slice(0, 2).map(event => (
                  <div
                    key={event.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    className="event-item bg-klorah-primary/80 text-white text-xs p-1 rounded cursor-pointer hover:bg-klorah-primary transition-all truncate"
                  >
                    {event.title || event.summary || 'Untitled Event'}
                  </div>
                ))}
                {dayEvents.length > 2 && (
                  <div className="text-xs text-klorah-text-light">
                    +{dayEvents.length - 2} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalendarDashboard() {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    location: '',
    startDate: '',
    endDate: '',
    isAllDay: false
  });

  const API_BASE = 'https://klorah-fast-server.onrender.com/api';

  useEffect(() => {
    if (currentUser) {
      fetchConnectionStatus();
      fetchEvents();
    }
  }, [currentUser]);

  const fetchConnectionStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/connection-status`, {
        headers: { 'instructor-id': currentUser.uid }
      });
      const data = await response.json();
      console.log('Calendar connection status response:', data);
      
      // Debug logging for connection status logic
      console.log('Connection status debug:', {
        connected: data.connected,
        autoDisconnectedReason: data.autoDisconnectedReason,
        shouldShowConnect: data.connected !== true || data.autoDisconnectedReason,
        shouldShowReconnect: data.autoDisconnectedReason
      });
      
      // Check if the account was auto-disconnected due to token issues
      if (data.autoDisconnectedReason && data.connected === false) {
        setError(`Calendar disconnected: ${data.autoDisconnectedReason}. Please reconnect your Google Calendar.`);
      }
      
      setConnectionStatus(data);
    } catch (error) {
      console.error('Error fetching connection status:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/sessions/calendar/list-events`, {
        headers: { 'instructor-id': currentUser.uid }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Calendar events response:', data);
        console.log('Events array:', data.events);
        console.log('Number of events:', data.events?.length || 0);
        
        // Log each event for debugging
        if (data.events && data.events.length > 0) {
          data.events.forEach((event, index) => {
            console.log(`Event ${index + 1}:`, {
              id: event.id,
              title: event.title,
              startDate: event.startDate,
              endDate: event.endDate,
              isAllDay: event.isAllDay
            });
          });
        }
        
        setEvents(data.events || []);
        setError('');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to fetch calendar events');
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to connect to calendar service');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const connectToCalendar = async () => {
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/instructor-auth-url?user_id=${currentUser.uid}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'instructor-id': currentUser.uid 
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.open(data.authorization_url, '_blank');
      }
    } catch (error) {
      console.error('Error connecting to calendar:', error);
      setError('Failed to start calendar connection');
    }
  };

  const clearTokens = async () => {
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/clear-tokens`, {
        method: 'POST',
        headers: { 'instructor-id': currentUser.uid }
      });
      
      if (response.ok) {
        const data = await response.json();
        setError(''); // Clear error on success
        
        // Refresh connection status
        await fetchConnectionStatus();
        
        // Show success message briefly
        setTimeout(() => {
          setError('Tokens cleared. Please reconnect your Google Calendar.');
        }, 100);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to clear tokens');
      }
    } catch (error) {
      console.error('Error clearing tokens:', error);
      setError('Failed to clear tokens. Please try again.');
    }
  };

  const createEvent = async (eventData) => {
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/create-calendar-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'instructor-id': currentUser.uid
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        const result = await response.json();
        setError('');
        fetchEvents(); // Refresh events
        return result;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create event');
        throw new Error(errorData.detail);
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Failed to create calendar event');
      throw error;
    }
  };

  const updateEvent = async (eventId, eventData) => {
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/update-calendar-event/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'instructor-id': currentUser.uid
        },
        body: JSON.stringify(eventData)
      });

      if (response.ok) {
        const result = await response.json();
        setError('');
        fetchEvents(); // Refresh events
        return result;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update event');
        throw new Error(errorData.detail);
      }
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update calendar event');
      throw error;
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      const response = await fetch(`${API_BASE}/sessions/calendar/delete-calendar-event/${eventId}`, {
        method: 'DELETE',
        headers: { 'instructor-id': currentUser.uid }
      });

      if (response.ok) {
        setError('');
        fetchEvents(); // Refresh events
        return true;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete event');
        return false;
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Failed to delete calendar event');
      return false;
    }
  };

  const handleEventCreate = (draggedEvent) => {
    setNewEvent({
      title: '',
      description: '',
      location: '',
      startDate: draggedEvent.startDate,
      endDate: draggedEvent.endDate,
      isAllDay: false
    });
    setShowCreateModal(true);
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.startDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const eventData = {
        title: newEvent.title,
        description: newEvent.description || '',
        location: newEvent.location || '',
        startDate: newEvent.startDate,
        endDate: newEvent.isAllDay ? newEvent.startDate : (newEvent.endDate || newEvent.startDate),
        isAllDay: newEvent.isAllDay,
        attendees: []
      };

      const response = await createEvent(eventData);
      
      if (response.success) {
        setShowCreateModal(false);
        setNewEvent({
          title: '',
          description: '',
          location: '',
          startDate: '',
          endDate: '',
          isAllDay: false
        });
        await fetchEvents(); // Refresh events
      } else {
        setError('Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setError('Failed to create event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent.title || !selectedEvent.startDate) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const eventData = {
        title: selectedEvent.title,
        description: selectedEvent.description || '',
        location: selectedEvent.location || '',
        startDate: selectedEvent.startDate,
        endDate: selectedEvent.isAllDay ? selectedEvent.startDate : (selectedEvent.endDate || selectedEvent.startDate),
        isAllDay: selectedEvent.isAllDay || false,
        attendees: selectedEvent.attendees || []
      };

      const response = await updateEvent(selectedEvent.id, eventData);
      
      if (response.success) {
        setShowEditModal(false);
        setSelectedEvent(null);
        await fetchEvents(); // Refresh events
      } else {
        setError('Failed to update event');
      }
    } catch (error) {
      console.error('Error updating event:', error);
      setError('Failed to update event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const success = await deleteEvent(selectedEvent.id);
      if (success) {
        setShowEditModal(false);
        setSelectedEvent(null);
      }
    }
  };

  const handleDateClick = (date) => {
    const dateStr = date.toISOString().slice(0, 10) + 'T09:00';
    const endDateStr = date.toISOString().slice(0, 10) + 'T10:00';
    
    setNewEvent({
      title: '',
      description: '',
      location: '',
      startDate: dateStr,
      endDate: endDateStr,
      isAllDay: false
    });
    setShowCreateModal(true);
  };

  const handleEventClick = (event) => {
    // Convert Google Calendar API format to our internal format for editing
    const formattedEvent = {
      ...event,
      title: event.title || event.summary || '',
      description: event.description || '',
      location: event.location || '',
      startDate: event.start?.dateTime || event.start?.date || event.startDate,
      endDate: event.end?.dateTime || event.end?.date || event.endDate,
      isAllDay: !event.start?.dateTime && event.start?.date
    };
    
    console.log('Original event:', event);
    console.log('Formatted event for editing:', formattedEvent);
    
    setSelectedEvent(formattedEvent);
    setShowEditModal(true);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-klorah-background flex items-center justify-center">
        <div className="text-white text-lg">Please log in to view your calendar.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-klorah-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Calendar Header */}
        <CalendarHeader 
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          onViewChange={setCurrentView}
          currentView={currentView}
          connectionStatus={connectionStatus}
          onConnect={connectToCalendar}
          onClearTokens={clearTokens}
        />

        {/* Error Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-klorah-primary"></div>
          </div>
        ) : (
          /* Interactive Calendar Grid */
          <InteractiveCalendarGrid 
            currentDate={currentDate}
            events={events}
            onEventClick={handleEventClick}
            onDateClick={handleDateClick}
            onEventCreate={handleEventCreate}
            currentView={currentView}
          />
        )}

        {/* Create Event Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-klorah-card border border-klorah-border rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-white">Create New Event</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-klorah-text hover:text-white transition-all p-2 hover:bg-klorah-dark rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleCreateEvent(); }} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    EVENT TITLE *
                  </label>
                  <input
                    type="text"
                    required
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                    placeholder="Enter event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    DESCRIPTION
                  </label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary h-24 resize-none"
                    placeholder="Add event description..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    LOCATION
                  </label>
                  <input
                    type="text"
                    value={newEvent.location || ''}
                    onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                    placeholder="Enter location (optional)"
                  />
                </div>

                {/* All Day Toggle */}
                <div className="flex items-center justify-between p-3 bg-klorah-dark border border-klorah-border rounded-xl">
                  <span className="text-white text-sm">All day event</span>
                  <button
                    type="button"
                    onClick={() => setNewEvent({...newEvent, isAllDay: !newEvent.isAllDay})}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      newEvent.isAllDay ? 'bg-klorah-primary' : 'bg-klorah-border'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      newEvent.isAllDay ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Enhanced Date/Time Pickers */}
                <div className="space-y-4">
                  <EnhancedDateTimePicker
                    value={newEvent.startDate}
                    onChange={(date) => setNewEvent({...newEvent, startDate: date})}
                    label="Start Date & Time"
                    required
                    isAllDay={newEvent.isAllDay}
                    placeholder="Select start date and time"
                  />

                  {!newEvent.isAllDay && (
                    <EnhancedDateTimePicker
                      value={newEvent.endDate}
                      onChange={(date) => setNewEvent({...newEvent, endDate: date})}
                      label="End Date & Time"
                      required
                      minDate={newEvent.startDate}
                      isAllDay={newEvent.isAllDay}
                      placeholder="Select end date and time"
                    />
                  )}
                </div>

                {/* Duration Presets - Only show if not all day */}
                {!newEvent.isAllDay && (
                  <div className="space-y-2">
                    <label className="text-xs text-klorah-text-light">Quick duration:</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: '15 min', minutes: 15 },
                        { label: '30 min', minutes: 30 },
                        { label: '1 hour', minutes: 60 },
                        { label: '2 hours', minutes: 120 },
                        { label: '4 hours', minutes: 240 },
                      ].map(preset => (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => {
                            if (newEvent.startDate) {
                              const start = new Date(newEvent.startDate);
                              const end = new Date(start.getTime() + preset.minutes * 60 * 1000);
                              setNewEvent({...newEvent, endDate: end.toISOString().slice(0, 16)});
                            }
                          }}
                          className="px-3 py-1 text-xs bg-klorah-primary/20 text-klorah-primary rounded-lg hover:bg-klorah-primary/30 transition-all"
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-3 border border-klorah-border text-klorah-text rounded-xl hover:bg-klorah-dark transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-klorah-primary text-white rounded-xl hover:bg-klorah-primary/80 transition-all font-medium"
                  >
                    Create Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Event Modal */}
        {showEditModal && selectedEvent && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-klorah-card border border-klorah-border rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-white">Edit Event</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-klorah-text hover:text-white transition-all p-2 hover:bg-klorah-dark rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleUpdateEvent(); }} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    EVENT TITLE *
                  </label>
                  <input
                    type="text"
                    required
                    value={selectedEvent.title || ''}
                    onChange={(e) => setSelectedEvent({...selectedEvent, title: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    DESCRIPTION
                  </label>
                  <textarea
                    value={selectedEvent.description || ''}
                    onChange={(e) => setSelectedEvent({...selectedEvent, description: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary h-24 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
                    LOCATION
                  </label>
                  <input
                    type="text"
                    value={selectedEvent.location || ''}
                    onChange={(e) => setSelectedEvent({...selectedEvent, location: e.target.value})}
                    className="w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
                    placeholder="Enter location (optional)"
                  />
                </div>

                {/* All Day Toggle */}
                <div className="flex items-center justify-between p-3 bg-klorah-dark border border-klorah-border rounded-xl">
                  <span className="text-white text-sm">All day event</span>
                  <button
                    type="button"
                    onClick={() => setSelectedEvent({...selectedEvent, isAllDay: !selectedEvent.isAllDay})}
                    className={`relative w-12 h-6 rounded-full transition-all ${
                      selectedEvent.isAllDay ? 'bg-klorah-primary' : 'bg-klorah-border'
                    }`}
                  >
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      selectedEvent.isAllDay ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>

                {/* Enhanced Date/Time Pickers */}
                <div className="space-y-4">
                  <EnhancedDateTimePicker
                    value={selectedEvent.startDate}
                    onChange={(date) => setSelectedEvent({...selectedEvent, startDate: date})}
                    label="Start Date & Time"
                    required
                    isAllDay={selectedEvent.isAllDay || false}
                  />

                  {!selectedEvent.isAllDay && (
                    <EnhancedDateTimePicker
                      value={selectedEvent.endDate}
                      onChange={(date) => setSelectedEvent({...selectedEvent, endDate: date})}
                      label="End Date & Time"
                      required
                      minDate={selectedEvent.startDate}
                      isAllDay={selectedEvent.isAllDay || false}
                    />
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleDeleteEvent}
                    className="px-4 py-3 bg-red-500/20 text-red-400 border border-red-500/50 rounded-xl hover:bg-red-500/30 transition-all"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-3 border border-klorah-border text-klorah-text rounded-xl hover:bg-klorah-dark transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-klorah-primary text-white rounded-xl hover:bg-klorah-primary/80 transition-all font-medium"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarDashboard; 